import { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  Archive,
  ArrowLeft,
  CircleStop,
  FileDown,
  Loader2,
  Pencil,
  Play,
  Send,
  Trash2,
} from 'lucide-react';
import { isAxiosError } from 'axios';
import { Button } from '@/app/components/ui/button';
import { Badge } from '@/app/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/app/components/ui/card';
import { Skeleton } from '@/app/components/ui/skeleton';
import { Alert, AlertDescription, AlertTitle } from '@/app/components/ui/alert';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/app/components/ui/table';
import {
  useActivateSafeWorkPlan,
  useApproveSafeWorkPlan,
  useArchiveSafeWorkPlan,
  useCloseSafeWorkPlan,
  useDeleteSafeWorkPlan,
  useReturnSafeWorkPlanForChanges,
  useSafeWorkPlan,
  useSubmitSafeWorkPlanForReview,
} from '@/hooks/use-safety';
import { usePermissions } from '@/hooks/use-permissions';
import { useAuth } from '@/contexts/auth-context';
import { apiClient } from '@/lib/api-client';
import { filenameFromContentDisposition } from '@/lib/download';
import { toast } from '@/app/components/ui/simple-toaster';
import { useOrgTimeZone } from '@/hooks/use-settings';
import { formatDateOnly, formatDateTimeInTimeZone } from '@/lib/date-utils';
import type { SafeWorkPlanStatus } from '@/lib/types';
import { SafetyDiagramsSection } from '../safety-diagrams-section';
import { CrewInitialsCard } from '@/app/components/safety/crew-initials-card';
import {
  ReturnForChangesDialog,
  RevisionWarningDialog,
} from '@/app/components/safety/plan-action-dialogs';

const statusColors: Record<SafeWorkPlanStatus, string> = {
  draft: 'bg-gray-100 text-gray-800',
  pending_review: 'bg-yellow-100 text-yellow-800',
  approved: 'bg-green-100 text-green-800',
  active: 'bg-blue-100 text-blue-800',
  closed: 'bg-gray-100 text-gray-600',
  archived: 'bg-gray-100 text-gray-500',
};

export function WorkPlanDetailPage() {
  const timeZone = useOrgTimeZone();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const planId = parseInt(id || '0', 10);
  const { data: plan, isLoading } = useSafeWorkPlan(planId);
  const approveMutation = useApproveSafeWorkPlan();
  const activateMutation = useActivateSafeWorkPlan();
  const closeMutation = useCloseSafeWorkPlan();
  const archiveMutation = useArchiveSafeWorkPlan();
  const deleteMutation = useDeleteSafeWorkPlan();
  const returnMutation = useReturnSafeWorkPlanForChanges();
  const submitForReviewMutation = useSubmitSafeWorkPlanForReview();
  const { isAdmin, hasMinModuleRole } = usePermissions();
  const { user } = useAuth();
  const [downloading, setDownloading] = useState(false);
  const [showRevisionWarning, setShowRevisionWarning] = useState(false);
  const [showReturnDialog, setShowReturnDialog] = useState(false);
  const canManageSafety = hasMinModuleRole('safety', 'manager');

  const handleDownloadPdf = async () => {
    if (!plan) return;
    setDownloading(true);
    try {
      const response = await apiClient.get(`/api/v1/safety/work-plans/${plan.id}/pdf/`, {
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute(
        'download',
        filenameFromContentDisposition(
          response.headers['content-disposition'],
          `SafeWorkPlan-${plan.id}.pdf`,
        ),
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      // silently fail
    } finally {
      setDownloading(false);
    }
  };

  const handleDelete = () => {
    if (!window.confirm('Delete this draft safe work plan?')) return;
    deleteMutation.mutate(planId, {
      onSuccess: () => navigate('/safety/work-plans'),
      onError: (error) =>
        toast.error(
          isAxiosError<{ detail?: string }>(error) && error.response?.data?.detail
            ? error.response.data.detail
            : 'Failed to delete work plan.',
        ),
    });
  };

  if (isLoading) {
    return (
      <div className="p-4 sm:p-6 lg:p-8 space-y-6">
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (!plan) {
    return (
      <div className="p-8 text-center">
        <p className="text-gray-600">Work plan not found.</p>
        <Button asChild className="mt-4">
          <Link to="/safety/work-plans">Back to Work Plans</Link>
        </Button>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6 max-w-4xl mx-auto">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/safety/work-plans')}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{plan.title}</h1>
            <p className="text-gray-600">
              {plan.location} &middot; {formatDateOnly(plan.date)}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Badge className={plan.is_superseded ? 'bg-gray-100 text-gray-600' : statusColors[plan.status]}>
            {plan.is_superseded ? 'Superseded' : plan.status.replace('_', ' ')}
          </Badge>
          {plan.self_approved && <Badge variant="outline">Self-approved</Badge>}
          <Button variant="outline" size="sm" onClick={handleDownloadPdf} disabled={downloading}>
            {downloading ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <FileDown className="h-4 w-4 mr-1" />}
            PDF
          </Button>
        </div>
      </div>

      {plan.status === 'pending_review' && (
        <Alert>
          <Send className="h-4 w-4" />
          <AlertTitle>Awaiting approval</AlertTitle>
          <AlertDescription>{plan.review_route.message}</AlertDescription>
        </Alert>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Plan Details</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-gray-500">Project:</span> {plan.project_name || 'N/A'}
            </div>
            <div>
              <span className="text-gray-500">Project #:</span> {plan.project_number || 'N/A'}
            </div>
            <div>
              <span className="text-gray-500">Access Method:</span>{' '}
              {plan.access_method.replace('_', ' ')}
            </div>
            <div>
              <span className="text-gray-500">Created By:</span> {plan.created_by_username}
            </div>
            {plan.jsa_title && (
              <div>
                <span className="text-gray-500">Linked JSA:</span> {plan.jsa_title}
              </div>
            )}
            {plan.reviewed_by_username && (
              <div>
                <span className="text-gray-500">Reviewed By:</span> {plan.reviewed_by_username}
              </div>
            )}
          </div>
          {plan.scope_of_work && (
            <div>
              <p className="text-sm text-gray-500 mb-1">Scope of Work</p>
              <p className="text-sm whitespace-pre-wrap">{plan.scope_of_work}</p>
            </div>
          )}
          {plan.rigging_plan && (
            <div>
              <p className="text-sm text-gray-500 mb-1">Rigging Plan</p>
              <p className="text-sm whitespace-pre-wrap">{plan.rigging_plan}</p>
            </div>
          )}
          {plan.equipment_requirements && (
            <div>
              <p className="text-sm text-gray-500 mb-1">Equipment Requirements</p>
              <p className="text-sm whitespace-pre-wrap">{plan.equipment_requirements}</p>
            </div>
          )}
        </CardContent>
      </Card>

      {plan.steps.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Work Steps</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-12">#</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead>Safety Notes</TableHead>
                  <TableHead>Role</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {plan.steps.map((step) => (
                  <TableRow key={step.id}>
                    <TableCell className="font-medium">{step.step_number}</TableCell>
                    <TableCell>{step.description}</TableCell>
                    <TableCell className="text-gray-600">
                      {step.safety_considerations || '-'}
                    </TableCell>
                    <TableCell className="text-gray-600">
                      {step.responsible_role || '-'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {(plan.anchor_points_description ||
        plan.fall_distance_calculation ||
        plan.exclusion_zones ||
        plan.communication_plan ||
        plan.weather_limitations) && (
        <Card>
          <CardHeader>
            <CardTitle>Additional Details</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {plan.anchor_points_description && (
              <div>
                <p className="text-sm text-gray-500 mb-1">Anchor Points</p>
                <p className="text-sm whitespace-pre-wrap">{plan.anchor_points_description}</p>
              </div>
            )}
            {plan.fall_distance_calculation && (
              <div>
                <p className="text-sm text-gray-500 mb-1">Fall Distance Calculation</p>
                <p className="text-sm whitespace-pre-wrap">{plan.fall_distance_calculation}</p>
              </div>
            )}
            {plan.exclusion_zones && (
              <div>
                <p className="text-sm text-gray-500 mb-1">Exclusion Zones</p>
                <p className="text-sm whitespace-pre-wrap">{plan.exclusion_zones}</p>
              </div>
            )}
            {plan.communication_plan && (
              <div>
                <p className="text-sm text-gray-500 mb-1">Communication Plan</p>
                <p className="text-sm whitespace-pre-wrap">{plan.communication_plan}</p>
              </div>
            )}
            {plan.weather_limitations && (
              <div>
                <p className="text-sm text-gray-500 mb-1">Weather Limitations</p>
                <p className="text-sm whitespace-pre-wrap">{plan.weather_limitations}</p>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {plan.signatures.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Signatures ({plan.signatures.length})</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead>Signed At</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {plan.signatures.map((sig) => (
                  <TableRow key={sig.id}>
                    <TableCell className="font-medium">{sig.signer_name}</TableCell>
                    <TableCell>{sig.signer_role}</TableCell>
                    <TableCell>{formatDateTimeInTimeZone(sig.signed_at, timeZone)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      <CrewInitialsCard summary={plan.crew_initials} timeZone={timeZone} canInitial={['approved', 'active'].includes(plan.status)} />

      <SafetyDiagramsSection contentType="safeworkplan" objectId={plan.id} canEdit={plan.status === 'draft'} />

      <div className="flex justify-end gap-3">
        {plan.status === 'draft' && (isAdmin || user?.id === plan.created_by) && (
          <>
            <Button variant="outline" asChild>
              <Link to={`/safety/work-plans/${plan.id}/edit`}>
                <Pencil className="h-4 w-4 mr-1" />
                Edit
              </Link>
            </Button>
            {!plan.ever_submitted && (
              <Button
                variant="outline"
                onClick={handleDelete}
                disabled={deleteMutation.isPending}
              >
                <Trash2 className="h-4 w-4 mr-1" />
                {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
              </Button>
            )}
          </>
        )}
        {['approved', 'active', 'closed', 'archived'].includes(plan.status) &&
          plan.is_current &&
          canManageSafety && (
          <Button variant="outline" onClick={() => setShowRevisionWarning(true)}>
            <Pencil className="h-4 w-4 mr-1" />
            Create New Revision
          </Button>
        )}
        {plan.status === 'draft' && canManageSafety && (
          <Button
            onClick={() =>
              submitForReviewMutation.mutate(plan.id, {
                onSuccess: (updatedPlan) => {
                  toast.success(updatedPlan.review_route.message);
                },
                onError: (error) => {
                  const message =
                    isAxiosError<{ error?: string }>(error) && error.response?.data?.error
                      ? error.response.data.error
                      : 'Failed to submit for review.';
                  toast.error(message);
                },
              })
            }
            disabled={submitForReviewMutation.isPending}
          >
            {submitForReviewMutation.isPending ? (
              <Loader2 className="h-4 w-4 mr-1 animate-spin" />
            ) : (
              <Send className="h-4 w-4 mr-1" />
            )}
            {submitForReviewMutation.isPending ? 'Submitting...' : 'Submit for Review'}
          </Button>
        )}
        {plan.status === 'pending_review' && plan.can_approve && (
          <>
            <Button
              variant="outline"
              onClick={() => setShowReturnDialog(true)}
              disabled={returnMutation.isPending}
            >
              Return for changes
            </Button>
            <Button
              onClick={() =>
                approveMutation.mutate(plan.id, {
                  onError: (error) => {
                    const message =
                      isAxiosError<{ error?: string }>(error) && error.response?.data?.error
                        ? error.response.data.error
                        : 'Failed to approve work plan.';
                    toast.error(message);
                  },
                })
              }
              disabled={approveMutation.isPending}
            >
              {approveMutation.isPending ? 'Approving...' : 'Approve Work Plan'}
            </Button>
          </>
        )}
        {plan.status === 'approved' && canManageSafety && (
          <Button
            onClick={() => activateMutation.mutate(plan.id)}
            disabled={activateMutation.isPending}
          >
            <Play className="h-4 w-4 mr-1" />
            {activateMutation.isPending ? 'Activating...' : 'Activate Work Plan'}
          </Button>
        )}
        {plan.status === 'active' && canManageSafety && (
          <Button
            onClick={() => closeMutation.mutate(plan.id)}
            disabled={closeMutation.isPending}
          >
            <CircleStop className="h-4 w-4 mr-1" />
            {closeMutation.isPending ? 'Closing...' : 'Close Work Plan'}
          </Button>
        )}
        {plan.status === 'closed' && canManageSafety && (
          <Button
            onClick={() => archiveMutation.mutate(plan.id)}
            disabled={archiveMutation.isPending}
          >
            <Archive className="h-4 w-4 mr-1" />
            {archiveMutation.isPending ? 'Archiving...' : 'Archive Work Plan'}
          </Button>
        )}
      </div>
      <RevisionWarningDialog
        open={showRevisionWarning}
        onOpenChange={setShowRevisionWarning}
        nextVersion={(plan.version ?? 1) + 1}
        onContinue={() => navigate(`/safety/work-plans/${plan.id}/edit`)}
      />
      <ReturnForChangesDialog
        open={showReturnDialog}
        onOpenChange={setShowReturnDialog}
        isPending={returnMutation.isPending}
        onSubmit={(reason) =>
          returnMutation.mutate(
            { id: plan.id, reason },
            {
              onSuccess: () => {
                setShowReturnDialog(false);
                toast.success('Work plan returned for changes.');
              },
              onError: () => toast.error('Failed to return work plan for changes.'),
            },
          )
        }
      />
    </div>
  );
}
